/**
 * Decide whether AdSense should be asked to fill the ad slot inside `container`.
 *
 * AdSense fills each `<ins class="adsbygoogle">` exactly once and stamps it with a
 * `data-adsbygoogle-status` attribute afterwards. Calling `adsbygoogle.push({})`
 * again once every `<ins>` on the page is already filled makes AdSense throw a
 * benign `TagError` ("All 'ins' elements in the DOM with class=adsbygoogle already
 * have ads in them."). Because `runMotives()` re-runs on user engagement, settings
 * changes, ad rotation and prop updates, that error used to be raised — and logged
 * at ERROR — hundreds of thousands of times a day, flooding our logs.
 *
 * Guarding the push on the slot being present and not yet stamped prevents the
 * re-push (and therefore the error) at the source.
 */
export function canRequestAdSlot(container: Element | null | undefined): boolean {
    if (!container) {
        return false;
    }
    const ins = container.querySelector('ins.adsbygoogle');
    return !!ins && !ins.getAttribute('data-adsbygoogle-status') && slotInDocument(ins);
}

/**
 * Round 13 (Studio S, R12 QA N7 -- MEASURED on dev and QA, converter page → Studio tab): the two TagErrors
 * on every Studio open came from `requestAdFill` calls whose `<ins>` was no longer IN THE DOCUMENT -- the
 * converter's slots unmount when the Studio tab opens, and a push scheduled for one of them 400 ms earlier
 * still fired with a stamp-less, detached element (`document` held three `<ins>`, all `done`). AdSense
 * counts the elements in the DOM, so such a push finds nothing to fill and throws. A slot that is detached,
 * under a `hidden` ancestor, or (where the document reports layout at all) without a box is not one to
 * push for; jsdom reports no boxes for anything, so the box check is skipped there.
 */
export function slotInDocument(ins: Element): boolean {
    if (!ins.isConnected) return false;
    if (ins.closest('[hidden]')) return false;
    if (typeof document === 'undefined' || !document.body) return true;
    const layoutKnown = document.body.getClientRects().length > 0;
    return !layoutKnown || ins.getClientRects().length > 0;
}

/**
 * How long one `adsbygoogle.push({})` is assumed to be "in flight" for. AdSense fills EVERY
 * unfilled `<ins class="adsbygoogle">` in the document when it processes a push, so a second push
 * queued while the first is still pending finds nothing left to fill and throws the TagError
 * ("All 'ins' elements ... already have ads") -- the one every Studio page load raised while
 * `MotiveComponent` and `StudioAdComponent` each pushed for their own slot ~300 ms apart (S-50).
 */
export const AD_PUSH_WINDOW_MS = 2_000;
/** After a covered slot waits for the in-flight push, re-check this much later. */
export const AD_PUSH_RETRY_MS = 600;

let lastPushAt = 0;

/** Tests only. */
export function resetAdPushLatch(): void {
    lastPushAt = 0;
}

/**
 * Ask AdSense to fill the slot inside `container`, ONCE across every caller on the page.
 *
 *   - `pushed`  -- this call queued the push.
 *   - `covered` -- a push is already pending (queued < AD_PUSH_WINDOW_MS ago, or the AdSense script
 *                  has not loaded yet so the queued push will run on load): it will fill this slot
 *                  too. If the script IS loaded, the slot is re-checked after AD_PUSH_RETRY_MS and
 *                  pushed then if it is still unstamped (it mounted after the earlier push ran).
 *   - `skipped` -- no unstamped slot in the container.
 *
 * `now` and `retry` are injectable for tests.
 */
export function requestAdFill(
    container: Element | null | undefined,
    now: () => number = Date.now,
    retry: (fn: () => void, ms: number) => void = (fn, ms) => { setTimeout(fn, ms); },
): 'pushed' | 'covered' | 'skipped' {
    if (!canRequestAdSlot(container)) return 'skipped';
    const w = typeof window === 'undefined' ? null : (window as any);
    if (!w) return 'skipped';
    const queue = (w.adsbygoogle = w.adsbygoogle || []);
    const t = now();
    if (lastPushAt > 0 && t - lastPushAt < AD_PUSH_WINDOW_MS) {
        // A push is in flight. If the script has not loaded yet the queued push will process this
        // slot on load; if it has, it may already have run before this slot existed -- look again.
        if (queue.loaded === true) {
            retry(() => { if (canRequestAdSlot(container)) requestAdFill(container, now, retry); }, AD_PUSH_RETRY_MS);
        }
        return 'covered';
    }
    queue.push({});
    lastPushAt = t;
    return 'pushed';
}
